import { useEffect, useState, useCallback } from 'react';
import {
  Settings,
  HardDrive,
  Tags,
  Megaphone,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Plus,
  Trash2,
  X,
  Upload,
  Database,
  FolderSync,
  FileText,
  Wrench,
} from 'lucide-react';
import { siteProxy } from '../../services/siteProxy';

// ─── Types ───────────────────────────────────────────────────────────

interface Category {
  id: string;
  name: string;
  keywords: string[];
  prefixes: string[];
  is_active: boolean;
  priority: number;
}

interface Update {
  id: string;
  title: string;
  body: string;
  version?: string;
  created_at: string;
}

interface S3Status {
  s3Enabled: boolean;
  endpoint: string;
  bucket: string;
  cdnUrl: string;
  keyPrefix: string;
}

interface MigrationStatus {
  existing: { cloud: number; thumbnails: number; source: number; publication: number };
  pending: { cloud: number; thumbnails: number; source: number; publication: number; total: number };
  running: boolean;
}

interface MissingItem {
  article: string;
  print_name: string;
  folder: string;
}

interface LegacyItem {
  article: string;
  folder: string;
  expected: string;
}

interface BadArticle {
  article: string;
  corrected: string;
  folder: string;
  conflict: boolean;
}

// ─── Collapsible Section ─────────────────────────────────────────────

function Section({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-white/[0.06] bg-slate-800/40 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition"
      >
        <Icon className="w-4.5 h-4.5 text-slate-400" />
        <span className="text-base font-medium text-slate-200 flex-1">{title}</span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500" />
        )}
      </button>
      {open && <div className="px-5 pb-5 border-t border-white/[0.04]">{children}</div>}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────

export function MockupLabPage() {
  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Mockup Lab</h1>
        <p className="text-sm text-slate-500 mt-1">Настройки mockup.witelab.ru</p>
      </div>

      <Section title="Облачные категории" icon={Tags} defaultOpen>
        <CategoriesPanel />
      </Section>

      <Section title="Sync имён" icon={FileText}>
        <SyncPanel />
      </Section>

      <Section title="Миграция папок" icon={FolderSync}>
        <FolderMigrationPanel />
      </Section>

      <Section title="Исправление артикулов" icon={Wrench}>
        <ArticleFixPanel />
      </Section>

      <Section title="Обновления" icon={Megaphone}>
        <UpdatesPanel />
      </Section>

      <Section title="S3 хранилище" icon={HardDrive}>
        <S3Panel />
      </Section>
    </div>
  );
}

// ─── Categories Panel ────────────────────────────────────────────────

function CategoriesPanel() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [newKeywords, setNewKeywords] = useState('');
  const [newPrefixes, setNewPrefixes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('mockup', '/api/admin/load-data')) as {
        categories?: Category[];
      };
      setCategories(data?.categories || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (cats: Category[]) => {
    setSaving(true);
    await siteProxy('mockup', '/api/admin/save-categories', {
      method: 'POST',
      body: { categories: cats },
    });
    setSaving(false);
    load();
  };

  const addCategory = () => {
    if (!newName.trim()) return;
    const cat: Category = {
      id: '',
      name: newName.trim(),
      keywords: newKeywords
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      prefixes: newPrefixes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      is_active: true,
      priority: categories.length,
    };
    save([...categories, cat]);
    setAdding(false);
    setNewName('');
    setNewKeywords('');
    setNewPrefixes('');
  };

  const deleteCategory = (id: string) => {
    if (!confirm('Удалить категорию?')) return;
    save(categories.filter((c) => c.id !== id));
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-3">
      <div className="flex justify-end">
        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-violet-600 hover:bg-violet-500 text-white transition"
        >
          <Plus className="w-3.5 h-3.5" />
          Добавить
        </button>
      </div>

      {adding && (
        <div className="p-4 rounded-lg bg-slate-900/60 border border-violet-500/20 space-y-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="input"
            placeholder="Название категории"
            autoFocus
          />
          <input
            type="text"
            value={newKeywords}
            onChange={(e) => setNewKeywords(e.target.value)}
            className="input"
            placeholder="Ключевые слова (через запятую)"
          />
          <input
            type="text"
            value={newPrefixes}
            onChange={(e) => setNewPrefixes(e.target.value)}
            className="input"
            placeholder="Префиксы (через запятую)"
          />
          <div className="flex gap-2">
            <button
              onClick={addCategory}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg text-xs bg-violet-600 text-white hover:bg-violet-500 transition disabled:opacity-50"
            >
              Сохранить
            </button>
            <button
              onClick={() => setAdding(false)}
              className="px-3 py-1.5 rounded-lg text-xs bg-slate-800 text-slate-400 hover:bg-slate-700 transition"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {categories.length === 0 ? (
        <div className="py-6 text-center text-slate-600 text-sm">Нет категорий</div>
      ) : (
        categories.map((cat) => (
          <div
            key={cat.id || cat.name}
            className="flex items-center justify-between p-3 rounded-lg bg-slate-900/60 border border-white/[0.06]"
          >
            <div>
              <span className="text-sm font-medium text-slate-200">{cat.name}</span>
              {cat.keywords.length > 0 && (
                <p className="text-xs text-slate-500 mt-0.5">
                  Ключевые: {cat.keywords.join(', ')}
                </p>
              )}
              {cat.prefixes.length > 0 && (
                <p className="text-xs text-slate-600 mt-0.5">
                  Префиксы: {cat.prefixes.join(', ')}
                </p>
              )}
            </div>
            <button
              onClick={() => deleteCategory(cat.id)}
              className="p-1.5 rounded text-slate-500 hover:text-red-400 transition"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))
      )}
    </div>
  );
}

// ─── Updates Panel ───────────────────────────────────────────────────

function UpdatesPanel() {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [version, setVersion] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('mockup', '/api/updates')) as Update[] | { updates?: Update[] };
      setUpdates(Array.isArray(data) ? data : (data as { updates?: Update[] }).updates || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createUpdate = async () => {
    if (!title.trim() || !body.trim()) return;
    await siteProxy('mockup', '/api/updates', {
      method: 'POST',
      body: { title: title.trim(), body: body.trim(), version: version.trim() || undefined },
    });
    setCreating(false);
    setTitle('');
    setBody('');
    setVersion('');
    load();
  };

  const deleteUpdate = async (id: string) => {
    if (!confirm('Удалить обновление?')) return;
    await siteProxy('mockup', '/api/updates', {
      method: 'DELETE',
      body: { id },
    });
    load();
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-3">
      <div className="flex justify-end">
        <button
          onClick={() => setCreating(!creating)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-amber-600 hover:bg-amber-500 text-white transition"
        >
          <Plus className="w-3.5 h-3.5" />
          Новое
        </button>
      </div>

      {creating && (
        <div className="p-4 rounded-lg bg-slate-900/60 border border-amber-500/20 space-y-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input"
            placeholder="Заголовок"
            autoFocus
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="input min-h-[80px] resize-y"
            placeholder="Текст обновления"
          />
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            className="input"
            placeholder="Версия (необязательно)"
          />
          <div className="flex gap-2">
            <button
              onClick={createUpdate}
              className="px-3 py-1.5 rounded-lg text-xs bg-amber-600 text-white hover:bg-amber-500 transition"
            >
              Создать
            </button>
            <button
              onClick={() => setCreating(false)}
              className="px-3 py-1.5 rounded-lg text-xs bg-slate-800 text-slate-400 hover:bg-slate-700 transition"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {updates.length === 0 ? (
        <div className="py-6 text-center text-slate-600 text-sm">Нет обновлений</div>
      ) : (
        updates.map((upd) => (
          <div
            key={upd.id}
            className="flex items-start justify-between p-3 rounded-lg bg-slate-900/60 border border-white/[0.06]"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-200">{upd.title}</span>
                {upd.version && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-400">
                    v{upd.version}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{upd.body}</p>
              <p className="text-[10px] text-slate-600 mt-1">
                {new Date(upd.created_at).toLocaleDateString('ru-RU')}
              </p>
            </div>
            <button
              onClick={() => deleteUpdate(upd.id)}
              className="p-1.5 rounded text-slate-500 hover:text-red-400 transition flex-shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))
      )}
    </div>
  );
}

// ─── S3 Panel ────────────────────────────────────────────────────────

function S3Panel() {
  const [s3Status, setS3Status] = useState<S3Status | null>(null);
  const [migration, setMigration] = useState<MigrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [progress, setProgress] = useState<{
    phase: string;
    uploaded: number;
    total: number;
    errors: string[];
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s3, mig] = await Promise.all([
        siteProxy('mockup', '/api/admin/s3-status') as Promise<S3Status>,
        siteProxy('mockup', '/api/admin/s3-migration/status') as Promise<MigrationStatus>,
      ]);
      setS3Status(s3);
      setMigration(mig);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startMigration = async () => {
    if (!confirm('Начать миграцию файлов в S3?')) return;
    setMigrating(true);
    await siteProxy('mockup', '/api/admin/s3-migration/start', { method: 'POST' });

    // Poll progress
    const poll = setInterval(async () => {
      const raw = (await siteProxy('mockup', '/api/admin/s3-migration/progress')) as { progress: typeof progress };
      const p = raw?.progress ?? null;
      setProgress(p);
      if (p?.phase === 'done') {
        clearInterval(poll);
        setMigrating(false);
        load();
      }
    }, 2000);
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-4">
      {/* Status */}
      {s3Status && (
        <div className="p-4 rounded-lg bg-slate-900/60 border border-white/[0.06] space-y-2">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-200">Статус S3</span>
            <span
              className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${
                s3Status.s3Enabled
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : 'bg-slate-700 text-slate-500'
              }`}
            >
              {s3Status.s3Enabled ? 'Включен' : 'Отключен'}
            </span>
            <button
              onClick={async () => {
                const next = !s3Status.s3Enabled;
                if (!confirm(next
                  ? 'Включить S3? Файлы будут загружаться в облако. Требуется перезапуск сервера.'
                  : 'Выключить S3? Файлы будут сохраняться локально. Требуется перезапуск сервера.',
                )) return;
                setToggling(true);
                try {
                  await siteProxy('mockup', '/api/admin/s3-toggle', {
                    method: 'POST',
                    body: { enabled: next },
                  });
                  setS3Status((prev) => prev ? { ...prev, s3Enabled: next } : prev);
                } catch { /* ignore */ }
                setToggling(false);
              }}
              disabled={toggling}
              className={`ml-auto px-3 py-1 rounded-lg text-xs font-medium transition ${
                s3Status.s3Enabled
                  ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                  : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
              } disabled:opacity-50`}
            >
              {toggling ? '...' : s3Status.s3Enabled ? 'Выключить' : 'Включить'}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-slate-600">Endpoint:</span>{' '}
              <span className="text-slate-400">{s3Status.endpoint || '—'}</span>
            </div>
            <div>
              <span className="text-slate-600">Bucket:</span>{' '}
              <span className="text-slate-400">{s3Status.bucket || '—'}</span>
            </div>
            <div>
              <span className="text-slate-600">Prefix:</span>{' '}
              <span className="text-slate-400">{s3Status.keyPrefix || '—'}</span>
            </div>
            <div>
              <span className="text-slate-600">CDN:</span>{' '}
              <span className="text-slate-400">{s3Status.cdnUrl || '—'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Migration status */}
      {migration && (
        <div className="p-4 rounded-lg bg-slate-900/60 border border-white/[0.06]">
          <h3 className="text-sm font-medium text-slate-200 mb-3">Миграция файлов</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {(['cloud', 'thumbnails', 'source', 'publication'] as const).map((key) => (
              <div key={key} className="text-center p-2 rounded-lg bg-slate-950/60">
                <p className="text-xs text-slate-500 mb-1 capitalize">{key}</p>
                <p className="text-lg font-semibold text-slate-200">
                  {migration.existing[key]}
                </p>
                {migration.pending[key] > 0 && (
                  <p className="text-[10px] text-amber-400">+{migration.pending[key]} ожидает</p>
                )}
              </div>
            ))}
          </div>

          {migration.pending.total > 0 && (
            <button
              onClick={startMigration}
              disabled={migrating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {migrating ? 'Миграция...' : `Загрузить ${migration.pending.total} файлов`}
            </button>
          )}

          {progress && migrating && (
            <div className="mt-3 p-3 rounded-lg bg-slate-950/60 text-xs">
              <p className="text-slate-400">
                Фаза: {progress.phase} | {progress.uploaded}/{progress.total}
              </p>
              {progress.errors.length > 0 && (
                <p className="text-red-400 mt-1">Ошибки: {progress.errors.length}</p>
              )}
              <div className="mt-2 bg-slate-800 rounded-full h-1.5">
                <div
                  className="bg-indigo-500 rounded-full h-1.5 transition-all"
                  style={{
                    width: `${progress.total > 0 ? (progress.uploaded / progress.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sync Names Panel ────────────────────────────────────────────────

function SyncPanel() {
  const [items, setItems] = useState<MissingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('mockup', '/api/admin/load-data')) as {
        missingItems?: MissingItem[];
      };
      setItems(data?.missingItems || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    if (!items.length || !confirm(`Обновить ${items.length} артикулов?`)) return;
    setStatus('Синхронизация...');
    try {
      const res = (await siteProxy('mockup', '/api/admin/sync', {
        method: 'POST',
        body: { items },
      })) as { success?: boolean; synced?: number };
      setStatus(res?.success ? `Обновлено: ${res.synced}` : 'Ошибка синхронизации');
      load();
    } catch {
      setStatus('Ошибка при синхронизации');
    }
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-3">
      {items.length === 0 ? (
        <div className="py-6 text-center text-emerald-400/60 text-sm">Все имена синхронизированы</div>
      ) : (
        <>
          <div className="rounded-lg border border-white/[0.06] overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-800/90">
                <tr className="text-slate-400">
                  <th className="text-left px-4 py-2 font-medium">Артикул</th>
                  <th className="text-left px-4 py-2 font-medium">Имя</th>
                  <th className="text-left px-4 py-2 font-medium">Папка</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.article} className="border-t border-white/[0.04]">
                    <td className="px-4 py-2 text-slate-300 font-mono text-xs">{item.article}</td>
                    <td className="px-4 py-2 text-slate-400">{item.print_name}</td>
                    <td className="px-4 py-2 text-slate-600 text-xs">{item.folder}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={sync}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm transition"
            >
              Синхронизировать ({items.length})
            </button>
            {status && <span className="text-sm text-slate-400">{status}</span>}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Folder Migration Panel ─────────────────────────────────────────

function FolderMigrationPanel() {
  const [items, setItems] = useState<LegacyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [log, setLog] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('mockup', '/api/admin/load-data')) as {
        legacyItems?: LegacyItem[];
      };
      setItems(data?.legacyItems || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const migrate = async (dryRun: boolean) => {
    if (!dryRun && !confirm(`Выполнить миграцию ${items.length} папок?`)) return;
    setStatus(dryRun ? 'Пробный запуск...' : 'Миграция...');
    setLog(null);
    try {
      const res = (await siteProxy('mockup', '/api/admin/migrate', {
        method: 'POST',
        body: { dry_run: dryRun },
      })) as { success?: boolean; migrated?: number; log?: string[]; errors?: string[] };
      setStatus(
        res?.success
          ? `${dryRun ? 'Пробный' : 'Мигрировано'}: ${res.migrated}${res.errors?.length ? `, ошибок: ${res.errors.length}` : ''}`
          : 'Ошибка миграции',
      );
      if (res?.log) setLog(res.log);
      if (!dryRun) load();
    } catch {
      setStatus('Ошибка при миграции');
    }
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-3">
      {items.length === 0 ? (
        <div className="py-6 text-center text-emerald-400/60 text-sm">Нет legacy папок для миграции</div>
      ) : (
        <>
          <div className="rounded-lg border border-white/[0.06] overflow-hidden max-h-48 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-800/90">
                <tr className="text-slate-400">
                  <th className="text-left px-4 py-2 font-medium">Артикул</th>
                  <th className="text-left px-4 py-2 font-medium">Текущая</th>
                  <th className="text-left px-4 py-2 font-medium">Ожидаемая</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.article} className="border-t border-white/[0.04]">
                    <td className="px-4 py-2 text-slate-300 font-mono text-xs">{item.article}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{item.folder}</td>
                    <td className="px-4 py-2 text-indigo-400 text-xs">{item.expected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => migrate(true)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm transition"
            >
              Пробный запуск
            </button>
            <button
              onClick={() => migrate(false)}
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm transition"
            >
              Выполнить миграцию ({items.length})
            </button>
            {status && <span className="text-sm text-slate-400">{status}</span>}
          </div>
        </>
      )}

      {log && (
        <div className="rounded-lg bg-slate-950/60 border border-white/[0.06] p-3 max-h-48 overflow-y-auto">
          <p className="text-[10px] text-slate-600 mb-2">Лог:</p>
          {log.map((line, i) => (
            <p key={i} className="text-xs text-slate-500 font-mono">{line}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Article Fix Panel ──────────────────────────────────────────────

function ArticleFixPanel() {
  const [articles, setArticles] = useState<BadArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('mockup', '/api/admin/load-data')) as {
        badArticles?: BadArticle[];
      };
      setArticles(data?.badArticles || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fix = async () => {
    const fixable = articles.filter((a) => !a.conflict);
    if (!fixable.length) return;
    if (!confirm(`Переименовать ${fixable.length} артикулов?`)) return;
    setStatus('Исправление...');
    try {
      const res = (await siteProxy('mockup', '/api/admin/fix-articles', {
        method: 'POST',
        body: { items: fixable },
      })) as { success?: boolean; fixed?: number; errors?: string[] };
      setStatus(
        res?.success
          ? `Исправлено: ${res.fixed}${res.errors?.length ? `, ошибок: ${res.errors.length}` : ''}`
          : 'Ошибка',
      );
      load();
    } catch {
      setStatus('Ошибка при исправлении');
    }
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-3">
      {articles.length === 0 ? (
        <div className="py-6 text-center text-emerald-400/60 text-sm">Все артикулы корректны</div>
      ) : (
        <>
          <div className="rounded-lg border border-white/[0.06] overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-800/90">
                <tr className="text-slate-400">
                  <th className="text-left px-4 py-2 font-medium">Текущий</th>
                  <th className="text-left px-4 py-2 font-medium">Исправленный</th>
                  <th className="text-left px-4 py-2 font-medium">Статус</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.article} className="border-t border-white/[0.04]">
                    <td className="px-4 py-2 text-slate-300 font-mono text-xs">{a.article}</td>
                    <td className="px-4 py-2 text-indigo-400 font-mono text-xs">{a.corrected}</td>
                    <td className="px-4 py-2">
                      {a.conflict ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">конфликт</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">готов</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fix}
              disabled={articles.filter((a) => !a.conflict).length === 0}
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm transition disabled:opacity-50"
            >
              Исправить ({articles.filter((a) => !a.conflict).length})
            </button>
            {status && <span className="text-sm text-slate-400">{status}</span>}
          </div>
        </>
      )}
    </div>
  );
}
